import {
    Block,
    CHILD_OBJECT_INVALID,
    CHILD_REQUIRED,
    CHILD_TYPE_INVALID,
    FIRST_CHILD_TYPE_INVALID,
    NODE_DATA_INVALID,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
} from '@gitbook/slate';
import { assert } from '@gitbook/util-ts';

import { createHeading1, isHeading } from '../heading';
import {
    BLOCK_HEADING_1,
    BLOCK_IF,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    BLOCK_UPDATES_FORMATS,
} from '../types';
import { unwrapChildren, wrapInlinesInParagraph } from '../utils';
import { normalizeDate, normalizeTags, validateDateAsYYYYMMDD } from './utils';

export const schema = Schema.create({
    blocks: {
        [BLOCK_UPDATES]: {
            parent: { objects: ['document', 'block'], types: [undefined, BLOCK_IF] },
            nodes: [{ types: [BLOCK_UPDATE] }],
            isVoid: false,
            data: {
                format: (format) => format === undefined || BLOCK_UPDATES_FORMATS.includes(format),
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_TYPE_INVALID]: (change, context) => {
                    assert(Block.isBlock(context.node));

                    const updates = context.node;

                    if (updates.nodes.size === 1) {
                        return change.removeNodeByKey(updates.key);
                    }

                    return change.wrapBlockByKey(
                        context.child.key,
                        Block.create({ type: BLOCK_UPDATE })
                    );
                },
            }),
        },
        [BLOCK_UPDATE]: {
            parent: { types: [BLOCK_UPDATES] },
            nodes: [{ objects: ['block'], min: 1 }],
            first: { types: [BLOCK_HEADING_1] },
            isVoid: false,
            data: {
                date: validateDateAsYYYYMMDD,
                tags: (tags) => tags === undefined || normalizeTags(tags) === tags,
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_REQUIRED]: (change, context) => {
                    const block = context.node;
                    if (Block.isBlock(block)) {
                        change.insertNodeByKey(block.key, 0, createHeading1());
                    }
                },
                [FIRST_CHILD_TYPE_INVALID]: (change, context) => {
                    const block = context.node;
                    const child = (context as { child?: unknown }).child;

                    if (Block.isBlock(block)) {
                        // If first child is some other heading, we set that h2/h3 to the required h1
                        if (Block.isBlock(child) && isHeading(child)) {
                            change.setNodeByKey(
                                child.key,
                                { type: BLOCK_HEADING_1 },
                                { normalize: false }
                            );

                            return;
                        }
                        // Always fallback to having an empty heading-1 at the top if we don't have one
                        change.insertNodeByKey(block.key, 0, createHeading1());
                    }
                },
                [CHILD_OBJECT_INVALID]: (change, context) => {
                    if (!('key' in context.node)) {
                        return;
                    }
                    return wrapInlinesInParagraph(change, context.node.key);
                },
                [NODE_DATA_INVALID]: (change, context) => {
                    const { node, key } = context;
                    // Normalize dates so we know they are in YYYY-MM-DD format
                    if (key === 'date' && Block.isBlock(node)) {
                        const providedDate = node.data.get(key);
                        const dateYYYYMMDD = normalizeDate(providedDate);
                        change.setDataByKey(node.key, [key], dateYYYYMMDD);
                    }
                    // Normalize tags
                    if (key === 'tags' && Block.isBlock(node)) {
                        const providedTags = node.data.get('tags');
                        const normalizedTags = normalizeTags(providedTags);
                        change.setDataByKey(node.key, [key], normalizedTags);
                    }
                },
            }),
        },
    },
});
